/**
 * IP 검증 유틸리티 (Phase 7: IP 화이트리스트)
 *
 * **목적:**
 * - 백엔드 /checkIP API를 호출하여 현재 클라이언트 IP가 허용되는지 확인
 * - App.tsx 초기화 시 호출되어 접근 가능 여부 결정
 *
 * **동작 방식:**
 * 1. REACT_APP_FUNCTIONS_URL/checkIP 엔드포인트 호출
 * 2. 백엔드에서 X-Forwarded-For 헤더를 통해 실제 IP 추출
 * 3. ALLOWED_IPS 환경변수와 비교
 * 4. 허용 여부를 success 플래그로 반환
 *
 * **환경별 동작:**
 * - development: 모든 IP 허용 (백엔드에서 처리)
 * - production: 환경변수에 설정된 IP만 허용
 *
 * **에러 처리:**
 * - 네트워크 오류 시 개발 환경에서는 통과, 프로덕션에서는 차단
 */

// V216: 리전 통일 (us-central1 → asia-northeast3)
const FUNCTIONS_URL = process.env.REACT_APP_FUNCTIONS_URL || 'http://localhost:5001/ngle-works/asia-northeast3';

/**
 * IP 검증 결과 타입
 *
 * V82.7 Phase 2: networkType / requiresMfa 추가
 */
export interface IPCheckResult {
  /** 호환: success === allowed (true=internal allowed) */
  success: boolean;
  /** V82.7: 명시적 IP 허용 여부 */
  allowed?: boolean;
  /** V82.7: 'internal' | 'external' */
  networkType?: 'internal' | 'external';
  /** V82.7: 외부망이면 true (FE 가 MFA 플로우 분기) */
  requiresMfa?: boolean;

  message?: string;
  error?: string;
  clientIP?: string;
  environment?: string;
}

/**
 * 클라이언트 IP 검증
 *
 * **사용 예시:**
 * ```typescript
 * const result = await checkClientIP();
 * if (result.success) {
 *   // 정상 앱 렌더링
 * } else {
 *   // RestrictedAccessPage 표시
 * }
 * ```
 *
 * **응답 예시 (허용):**
 * ```json
 * {
 *   "success": true,
 *   "message": "접속이 허용된 IP입니다.",
 *   "clientIP": "59.13.192.250",
 *   "environment": "production"
 * }
 * ```
 *
 * **응답 예시 (거부):**
 * ```json
 * {
 *   "success": false,
 *   "error": "접근이 거부되었습니다. 사내망에서만 접속 가능합니다.",
 *   "clientIP": "123.123.123.123"
 * }
 * ```
 *
 * @returns IP 검증 결과
 */
export async function checkClientIP(): Promise<IPCheckResult> {
  // V28.2: 개발 환경에서는 API 호출 자체를 생략 (콘솔 오염 방지)
  if (process.env.REACT_APP_ENV === 'development' || window.location.hostname === 'localhost') {
    return {
      success: true,
      message: '개발 모드: IP 검증 생략',
    };
  }

  try {
    // V94: CORS 모드 명시
    const response = await fetch(`${FUNCTIONS_URL}/checkIP`, {
      method: 'GET',
      mode: 'cors',
      credentials: 'omit', // IP 체크는 인증 불필요
      headers: {
        'Accept': 'application/json',
      },
    });

    const data = await response.json();

    // V82.7 Phase 2: BE 가 항상 200 + allowed/networkType/requiresMfa 반환
    //   호환: 옛 BE 가 403 으로 응답하는 경우도 처리
    if (response.ok) {
      const allowed = typeof data.allowed === 'boolean' ? data.allowed : !!data.success;
      return {
        success: allowed,
        allowed,
        networkType: data.networkType ?? (allowed ? 'internal' : 'external'),
        requiresMfa: data.requiresMfa ?? !allowed,
        message: data.message,
        clientIP: data.clientIP,
        environment: data.environment,
      };
    } else {
      console.warn('[IP 검증] 🚫 접속 거부:', data.clientIP);
      console.warn('[IP 검증] 사유:', data.error);
      return {
        success: false,
        allowed: false,
        networkType: 'external',
        requiresMfa: true,
        error: data.error || '접근이 거부되었습니다.',
        clientIP: data.clientIP,
      };
    }
  } catch (error) {
    console.warn('[IP 검증] API 호출 실패:', error instanceof Error ? error.message : String(error));

    // 개발 환경에서는 통과시킴
    if (process.env.REACT_APP_ENV === 'development') {
      return {
        success: true,
        message: '개발 모드: IP 검증 생략',
      };
    }

    // 프로덕션 환경에서는 차단
    console.warn('[IP 검증] 프로덕션 모드: API 연결 실패로 접근 차단');
    return {
      success: false,
      error: 'IP 검증 서버에 연결할 수 없습니다.',
    };
  }
}
